import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import usePremiumAccess from '../hooks/usePremiumAccess';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { 
  ArrowLeft, BookOpen, Brain, Target, Award, CheckCircle2, 
  Sparkles, MessageSquare, FileText, Clock, Zap, Lock
} from 'lucide-react';
import LessonContentViewer from '../components/LessonContentViewer';
import ModuleAudioPlayer from '../components/ModuleAudioPlayer';
import PedagogicalObjectives from '../components/PedagogicalObjectives';
import ModuleFeedback from '../components/ModuleFeedback';
import '../styles/lesson-content-awwwards.css';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ModuleDetailV2 = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPremiumAccess } = usePremiumAccess();
  
  // Navigate back to the pillar's module list (or training dashboard as fallback)
  const handleBack = () => {
    if (module?.pillar_id) {
      navigate(`/pillar/${module.pillar_id}`);
    } else {
      navigate('/training');
    }
  };
  
  const [module, setModule] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 🎯 GOUVERNANCE PÉDAGOGIQUE: État pour vérifier si entraînement disponible
  const [trainingStatus, setTrainingStatus] = useState({
    isModuleCompleted: false,
    hasActiveObjective: true,
    canTrain: true  // RÈGLE AND: désactivé UNIQUEMENT si (100% ET pas d&apos;objectif)
  });
  
  // Check if module requires premium (based on centralized logic)
  const isPremiumRequired = !hasPremiumAccess;
  
  // Text zoom control state
  const [textSize, setTextSize] = useState(100);
  
  // Module audio reading state
  const [isModuleReading, setIsModuleReading] = useState(false);
  
  // Reading progress bar
  const { scrollYProgress } = useScroll();
  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Particles configuration
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Scroll to top when component mounts or module changes
    window.scrollTo(0, 0);
    fetchModuleData();
    fetchTrainingStatus();
  }, [id, user, navigate]);

  // 🎯 GOUVERNANCE PÉDAGOGIQUE: Vérifier si l&apos;entraînement est disponible
  const fetchTrainingStatus = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      // 🔒 CORRECTION: Utiliser le bon endpoint backend
      const response = await axios.get(`${API}/pedagogical/scenario/current/${id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      const data = response.data;
      const isCompleted = data.module_completed === true;
      const hasObjective = data.has_active_objective === true;
      
      // 🔒 RÈGLE AND STRICTE (QUALIOPI):
      // Désactivé UNIQUEMENT si (module 100% ET pas d&apos;objectif actif)
      // canTrain = true si: module < 100% OU objectif actif existe
      const canTrain = !isCompleted || hasObjective;
      
      setTrainingStatus({
        isModuleCompleted: isCompleted,
        hasActiveObjective: hasObjective,
        canTrain: canTrain,
        objectiveTitle: data.objective_title,
        progress: data.progress
      });
      
      console.log(`[ModuleDetailV2] 🎯 Training status: completed=${isCompleted}, hasObjective=${hasObjective}, canTrain=${canTrain}`);
    } catch (error) {
      console.warn('[ModuleDetailV2] Could not fetch training status, defaulting to enabled');
      // Par défaut, permettre l&apos;entraînement
      setTrainingStatus({
        isModuleCompleted: false,
        hasActiveObjective: true,
        canTrain: true
      });
    }
  };

  const fetchModuleData = async () => {
    try {
      const response = await axios.get(`${API}/modules-v2/${id}/content`);
      const { module, lessons, scenarios } = response.data;
      
      setModule(module);
      setLesson(lessons[0] || null);
      setScenario(scenarios[0] || null);
    } catch (error) {
      // Détecter si c'est une erreur 403 (Premium requis)
      if (error.response?.status === 403) {
        console.log('🔒 Premium access required - redirecting to subscription');
        // Redirection avec 'from' pour permettre retour
        navigate('/subscription', { state: { from: location.pathname } });
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  // NOTE: toggleComplete supprimé - Validation manuelle désactivée
  // Seul Owen peut valider les modules via évaluation pédagogique

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Débutant': { bg: '#dcfce7', text: '#15803d', glow: 'rgba(21, 128, 61, 0.2)' },
      'Intermédiaire': { bg: '#fef3c7', text: '#a16207', glow: 'rgba(161, 98, 7, 0.2)' },
      'Avancé': { bg: '#fecaca', text: '#b91c1c', glow: 'rgba(185, 28, 28, 0.2)' }
    };
    return colors[difficulty] || colors['Débutant'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FDFBF6] via-[#FFF8EA] to-[#FFF3D6]">
        <div className="text-center">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="rounded-full h-16 w-16 border-4 border-[#FFD86F] border-t-transparent mx-auto mb-4"
          />
          <p className="text-[#2B1854] font-semibold text-lg">Chargement du module...</p>
        </div>
      </div>
    );
  };

  const diffColors = getDifficultyColor(module?.difficulty);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF6] via-[#FFF8EA] to-[#FFF3D6] relative overflow-x-hidden">
      
      {/* Particles Background - Subtle Golden Particles */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false },
          background: { color: { value: 'transparent' } },
          fpsLimit: 60,
          particles: {
            number: { value: 30, density: { enable: true, value_area: 800 } },
            color: { value: '#FFD86F' },
            shape: { type: 'circle' },
            opacity: {
              value: 0.3,
              random: true,
              animation: { enable: true, speed: 0.5, minimumValue: 0.1, sync: false }
            },
            size: {
              value: 3,
              random: true,
              animation: { enable: true, speed: 2, minimumValue: 1, sync: false }
            },
            move: {
              enable: true,
              speed: 1,
              direction: 'top',
              random: true,
              straight: false,
              outModes: { default: 'out' }
            }
          },
          detectRetina: true
        }}
        className="absolute inset-0 z-0 pointer-events-none"
      />

      {/* Reading Progress Bar - Fixed Right */}
      <motion.div
        className="fixed right-4 top-20 bottom-20 w-1 bg-[#FFD86F]/20 rounded-full overflow-hidden z-50 hidden lg:block"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.div
          className="w-full bg-gradient-to-b from-[#FFD86F] via-[#FFB347] to-[#E879F9] rounded-full"
          style={{ scaleY, transformOrigin: 'top' }}
        />
      </motion.div>

      {/* Header avec Halo Doré */}
      <motion.header 
        className="sticky top-0 z-40 border-b border-[#FFD86F]/15 backdrop-blur-xl"
        style={{
          background: 'rgba(255, 255, 255, 0.7)',
          boxShadow: '0 4px 20px rgba(255, 216, 111, 0.1)'
        }}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <Button 
            data-testid="back-to-pillar"
            onClick={handleBack} 
            variant="ghost"
            className="gap-2 mb-4 text-[#3B1E73] hover:text-[#FFD86F] hover:bg-[#FFD86F]/10 transition-all group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Retour aux modules
          </Button>
          
          <div className="flex flex-col lg:flex-row items-start justify-between gap-6">
            <div className="flex-1">
              <motion.div 
                className="flex items-center gap-3 mb-4 flex-wrap"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Badge 
                  className="px-3 py-1 rounded-full font-semibold shadow-sm"
                  style={{ 
                    background: diffColors.bg, 
                    color: diffColors.text,
                    border: 'none',
                    boxShadow: `0 0 20px ${diffColors.glow}`
                  }}
                >
                  {module?.difficulty}
                </Badge>
                <Badge 
                  className="px-3 py-1 rounded-full font-semibold border-2"
                  style={{
                    background: 'rgba(255, 216, 111, 0.15)',
                    color: '#92400E',
                    borderColor: '#D97706',
                    boxShadow: '0 0 15px rgba(255, 216, 111, 0.3)'
                  }}
                >
                  Module {module?.order}
                </Badge>
              </motion.div>
              
              <motion.h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight"
                style={{
                  background: 'linear-gradient(to right, #3B1E73, #B8860B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: 'Playfair Display, serif'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {module?.title}
              </motion.h1>

              {/* Halo doré sous le titre */}
              <motion.div
                className="h-1 w-32 bg-gradient-to-r from-[#FFD86F] to-transparent rounded-full mb-6"
                style={{ boxShadow: '0 0 20px rgba(255, 216, 111, 0.5)' }}
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 128, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
              
              <motion.div 
                className="flex items-start gap-3 mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Target className="w-6 h-6 mt-1 text-[#D97706] flex-shrink-0 drop-shadow-md" />
                <p className="text-base sm:text-lg text-[#2B1854]">
                  <strong className="text-[#92400E] font-bold">Objectif:</strong>{' '}
                  <span className="text-gray-700 italic">{module?.objective}</span>
                </p>
              </motion.div>
              
              <motion.p 
                className="text-gray-600 text-base sm:text-lg leading-relaxed opacity-90"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.9 }}
                transition={{ delay: 0.5 }}
              >
                {module?.description}
              </motion.p>
            </div>
            
            <motion.div 
              className="w-full lg:w-auto"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-[#FFD86F]/15 p-6 shadow-[0_0_30px_rgba(255,216,111,0.15)]">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-[#8B5CF6]" />
                  <span className="text-sm font-semibold text-[#3B1E73]">Évaluation Owen</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Vos objectifs sont évalués automatiquement par Owen lors de vos sessions d&apos;entraînement.
                </p>
                <div className="mt-2 flex items-center gap-1.5 text-xs text-[#8B5CF6]">
                  <Target className="w-3.5 h-3.5" />
                  <span>Voir la progression ci-dessous</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Content Column */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Module Audio Player - Owen Voice Reader */}
            {!isPremiumRequired && module && (
              <ModuleAudioPlayer
                moduleId={id}
                moduleTitle={module.title}
                onStatusChange={(status) => {
                  // Désactiver Coach Vocal/Écrit pendant la lecture
                  setIsModuleReading(status === 'playing');
                }}
              />
            )}
            
            {/* Lesson Content - Carte Glassmorphism Premium */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -4 }}
            >
              <Card 
                className="border border-[#FFD86F]/15 overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 0 40px rgba(255, 216, 111, 0.15), inset 0 1px 3px rgba(255, 255, 255, 0.8)'
                }}
              >
                <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30">
                  <motion.div 
                    className="flex items-center gap-3"
                    whileHover={{ x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="p-2 bg-gradient-to-br from-[#FFD86F] to-[#FFB347] rounded-lg shadow-md">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-[#2B1854]">
                      Contenu de Formation
                    </CardTitle>
                  </motion.div>
                </CardHeader>
                <CardContent className="p-4 sm:p-8 md:p-10">
                  {lesson ? (
                    <div className="prose prose-lg max-w-none">
                      <LessonContentViewer 
                        lesson={lesson}
                        textSize={textSize}
                        onTextSizeChange={setTextSize}
                      />
                    </div>
                  ) : isPremiumRequired ? (
                    // Module nécessite Premium - Afficher CTA Premium au lieu de "Contenu en Préparation"
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="p-8 bg-gradient-to-br from-[#FFF8EA] via-[#FFD86F]/10 to-white/50 rounded-2xl border border-[#FFD86F]/30 shadow-lg">
                        <div className="text-center space-y-4">
                          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD86F] via-[#FF9A8B] to-[#FF6B95] shadow-xl mb-4">
                            <Sparkles className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-[#2B1854] mb-2">
                            🚀 Contenu Premium Exclusif
                          </h3>
                          <p className="text-gray-700 leading-relaxed text-lg">
                            Ce module fait partie de notre collection Premium. 
                            Débloquez l&apos;accès pour accélérer votre évolution de leader.
                          </p>
                          <button
                            onClick={() => navigate('/subscription', { state: { from: location.pathname } })}
                            className="mt-6 py-4 px-8 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#FFD86F] via-[#FF9A8B] to-[#FF6B95] hover:shadow-2xl hover:scale-105 transition-all duration-300 shadow-xl"
                            style={{
                              boxShadow: '0 10px 40px rgba(255, 154, 139, 0.4)'
                            }}
                          >
                            Découvrir Premium
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    // Module vraiment en préparation (pas de contenu disponible)
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="p-8 bg-white/50 rounded-2xl border border-[#FFD86F]/20 shadow-sm">
                        <h3 className="text-2xl font-bold text-[#2B1854] mb-4">Contenu en Préparation</h3>
                        <p className="text-gray-700 leading-relaxed text-lg">
                          Le contenu détaillé de ce module est en cours de finalisation par nos experts. 
                          Vous pouvez déjà utiliser l&apos;entraînement IA pour pratiquer les concepts.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Action Plan - Avec Micro-interactions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -4 }}
            >
              <Card 
                className="border border-[#FFD86F]/15 overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(16px)',
                  boxShadow: '0 0 30px rgba(255, 216, 111, 0.1)'
                }}
              >
                <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-[#8B5CF6] to-[#E879F9] rounded-lg shadow-md">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-[#2B1854]">
                      Plan d&apos;Action
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <div className="space-y-5">
                    {[
                      { num: 1, title: 'Cette semaine', desc: 'Identifiez 3 situations où appliquer ces concepts' },
                      { num: 2, title: 'Ce mois', desc: "Mesurez l'impact sur l'engagement de votre équipe" },
                      { num: 3, title: 'Suivi', desc: 'Ajustez votre approche selon les retours' }
                    ].map((item, idx) => (
                      <motion.div 
                        key={item.num}
                        className="flex items-start gap-4 p-4 rounded-xl bg-white/50 hover:bg-white/70 transition-all cursor-pointer group"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + idx * 0.1 }}
                        whileHover={{ x: 8, scale: 1.02 }}
                      >
                        <div 
                          className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform"
                          style={{
                            background: 'linear-gradient(135deg, #FFD86F 0%, #FFB347 100%)',
                            boxShadow: '0 4px 15px rgba(255, 216, 111, 0.4)'
                          }}
                        >
                          {item.num}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-[#2B1854] mb-1 text-lg">{item.title}</h4>
                          <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* 📝 FEEDBACK MODULE - Qualiopi Indicateur 32 */}
            <ModuleFeedback 
              moduleId={parseInt(id)} 
              moduleName={module?.title}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* AI Training - Boutons Premium avec Glow */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card 
                className="border border-[#FFD86F]/15 overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 0 30px rgba(255, 216, 111, 0.2)'
                }}
              >
                <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-[#3B1E73] to-[#8B5CF6] rounded-lg shadow-md">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-[#2B1854]">
                        Entraînement IA
                      </CardTitle>
                      <CardDescription className="text-gray-600 text-sm mt-1">
                        Choisissez votre mode d&apos;entraînement
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 space-y-4">
                  {/* 🔒 RÈGLE AND: Désactivé UNIQUEMENT si (module 100% ET pas d&apos;objectif actif) */}
                  {(() => {
                    // Condition de désactivation: module complété ET pas d&apos;objectif actif
                    const isTrainingDisabled = !trainingStatus.canTrain;
                    const isDisabled = isPremiumRequired || isModuleReading || isTrainingDisabled;
                    
                    return (
                      <>
                        {/* Message si module terminé et pas d&apos;objectif */}
                        {isTrainingDisabled && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-3 rounded-xl bg-green-50 border border-green-200 mb-4"
                          >
                            <div className="flex items-center gap-2 text-green-700">
                              <CheckCircle2 className="w-5 h-5" />
                              <div>
                                <p className="font-semibold text-sm">Module terminé ✓</p>
                                <p className="text-xs text-green-600">Tous les objectifs ont été validés par Owen.</p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                        
                        <motion.div whileHover={{ scale: isDisabled ? 1 : 1.03 }} whileTap={{ scale: isDisabled ? 1 : 0.98 }}>
                          <Button 
                            onClick={() => {
                              if (isModuleReading) {
                                toast.info('Arrêtez la lecture du module avant de lancer un entraînement.');
                                return;
                              }
                              if (isTrainingDisabled) {
                                toast.success('Module complètement validé ! Tous les objectifs sont maîtrisés.');
                                return;
                              }
                              isPremiumRequired ? navigate('/subscription', { state: { from: location.pathname } }) : navigate(`/entrainement-ecrit/${id}`);
                            }}
                            disabled={isDisabled}
                            className="w-full gap-3 justify-start h-14 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all relative overflow-hidden group"
                            style={{
                              background: isDisabled
                                ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                                : 'linear-gradient(135deg, #3B1E73 0%, #8B5CF6 100%)',
                              border: 'none',
                              opacity: isDisabled ? 0.7 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer'
                            }}
                            title={isTrainingDisabled ? 'Module complètement validé' : isModuleReading ? 'Lecture en cours' : ''}
                          >
                            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <MessageSquare className="w-5 h-5 relative z-10" />
                            <span className="flex-1 text-left relative z-10">Entraînement Écrit</span>
                            {isPremiumRequired && <span className="text-sm relative z-10">🔒</span>}
                            {isModuleReading && <span className="text-sm relative z-10">🎧</span>}
                            {isTrainingDisabled && <CheckCircle2 className="w-4 h-4 relative z-10 text-green-400" />}
                            {!isDisabled && <span className="text-sm opacity-75 relative z-10">✍️</span>}
                          </Button>
                        </motion.div>
                        
                        <motion.div whileHover={{ scale: isDisabled ? 1 : 1.03 }} whileTap={{ scale: isDisabled ? 1 : 0.98 }}>
                          <Button 
                            onClick={() => {
                              if (isModuleReading) {
                                toast.info('Arrêtez la lecture du module avant de lancer un entraînement.');
                                return;
                              }
                              if (isTrainingDisabled) {
                                toast.success('Module complètement validé ! Tous les objectifs sont maîtrisés.');
                                return;
                              }
                              isPremiumRequired ? navigate('/subscription', { state: { from: location.pathname } }) : navigate(`/coach-vocal?moduleId=${id}`);
                            }}
                            disabled={isDisabled}
                            className="w-full gap-3 justify-start h-14 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all relative overflow-hidden group"
                            style={{
                              background: isDisabled
                                ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                                : 'linear-gradient(135deg, #FFD86F 0%, #FFB347 100%)',
                              color: isDisabled ? '#fff' : '#1B0F3B',
                              border: 'none',
                              opacity: isDisabled ? 0.7 : 1,
                              cursor: isDisabled ? 'not-allowed' : 'pointer'
                            }}
                            title={isTrainingDisabled ? 'Module complètement validé' : isModuleReading ? 'Lecture en cours' : ''}
                          >
                            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <Sparkles className="w-5 h-5 relative z-10" />
                            <span className="flex-1 text-left relative z-10">Valider ma certification</span>
                            {isPremiumRequired && <span className="text-sm relative z-10">🔒</span>}
                            {isModuleReading && <span className="text-sm relative z-10">🎧</span>}
                            {isTrainingDisabled && <CheckCircle2 className="w-4 h-4 relative z-10 text-green-400" />}
                            {!isDisabled && <span className="text-sm opacity-75 relative z-10">🎙️</span>}
                          </Button>
                        </motion.div>
                      </>
                    );
                  })()}
                  
                  <div className="pt-4 border-t border-[#FFD86F]/15 space-y-2">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <strong className="text-[#6D28D9]">Mode Écrit:</strong> Chat interactif pour réflexion approfondie
                    </p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <strong className="text-[#B45309]">Mode Vocal:</strong> Coach IA incarné avec vidéo et voix
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* 🎓 BLOC OBJECTIFS PÉDAGOGIQUES - GOUVERNANCE OWEN */}
            {/* L'utilisateur ne peut RIEN modifier - Seul Owen valide */}
            <PedagogicalObjectives 
              moduleId={parseInt(id)} 
              moduleOrder={module?.order}
            />

            {/* Module Info - Animation Latérale */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Card 
                className="border border-[#FFD86F]/15 overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 0 20px rgba(255, 216, 111, 0.1)'
                }}
              >
                <CardHeader className="border-b border-[#FFD86F]/10 bg-white/30">
                  <CardTitle className="text-lg font-bold text-[#2B1854] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#FFD86F]" />
                    Informations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-5 space-y-3">
                  {[
                    { label: 'Durée estimée', value: '25-30 min', color: '#92400E' },
                    { label: 'Type', value: 'Formation + Pratique', color: '#6D28D9' },
                    { label: 'Niveau', value: module?.difficulty, color: diffColors.text }
                  ].map((info, idx) => (
                    <motion.div 
                      key={info.label}
                      className="flex justify-between items-center p-3 rounded-xl bg-white/40 hover:bg-white/60 transition-all cursor-pointer group"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + idx * 0.1 }}
                      whileHover={{ x: 4 }}
                    >
                      <span className="text-sm text-gray-600 font-medium">{info.label}:</span>
                      <span 
                        className="text-sm font-bold"
                        style={{ color: info.color }}
                      >
                        {info.value}
                      </span>
                    </motion.div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default ModuleDetailV2;
